import { useState, useRef, useMemo, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import { useClickOutside } from '../../../../../utils/hooks/useClickOutside';
import { useWindowSize } from '../../../../../utils/hooks/useWindowSize';
import ColorPicker from '../../../Style/ColorPicker';
import {
  CONNECT_BUTTON_TYPE_OPTIONS,
  connectButtonRequiresValue,
  connectButtonUsesOriginUrl,
  getConnectButtonColors,
  getConnectButtonDefaultIcon,
  getConnectButtonDefaultText,
  getConnectButtonIconChoices,
  getConnectButtonSelectionValue,
  getConnectButtonValuePlaceholder,
  shouldSyncConnectButtonPresetStyles,
} from '../../../../../lib/connectButtons';
import styles from './SortableRow.module.css';

const SOCIAL_SELECTIONS = new Set(['facebook', 'instagram', 'linkedin', 'x']);
const icons = (row) => getConnectButtonIconChoices(row);

export default function SortableRow({ id, row, onUpdate, onDelete, canDelete = true, allCollapsed, setAllCollapsed, containerWidth }) {
  const { listeners, transform, transition: sortTransition, isDragging, setNodeRef, setActivatorNodeRef } = useSortable({ id });
  const { width: windowWidth } = useWindowSize();
  
  const [ expanded, setExpanded ] = useState(false);
  const [ edit, setEdit ] = useState('');

  useEffect(() => {
    if (allCollapsed) {
      setExpanded(false)
    }
  },[allCollapsed])
  
  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
        sortTransition,
        opacity: isDragging ? 0.5 : undefined,
      }
    : undefined;

  const updateRow = (patch, removeKeys) => {
    onUpdate({ id: row.id, patch, removeKeys });
  };

  const handleTypeChange = (e) => {
    const selectionValue = e.target.value;
    const previousSelection = getConnectButtonSelectionValue(row);
    const icon = getConnectButtonDefaultIcon(selectionValue);
    const prevDefault = getConnectButtonDefaultText(previousSelection);
    const nextDefault = getConnectButtonDefaultText(selectionValue);
    const nextColors = getConnectButtonColors(selectionValue);
    const usesOriginUrl = connectButtonUsesOriginUrl(selectionValue);

    let nextText = row.text;
    // If text is empty or currently equal to the previous type's default,
    // set it to the new default for the newly selected type
    if (!nextText || nextText === prevDefault) {
      nextText = nextDefault;
    }

    const patch = {
      type: SOCIAL_SELECTIONS.has(selectionValue) ? 'redirect' : selectionValue,
      icon,
      text: nextText,
      value: usesOriginUrl ? '' : row.value || '',
    };
    if (SOCIAL_SELECTIONS.has(selectionValue)) {
      patch.platform = selectionValue;
    }
    if (shouldSyncConnectButtonPresetStyles(row, previousSelection)) {
      patch.background = nextColors.background;
      patch.color = nextColors.color;
    }

    const removeKeys = [];
    if (!SOCIAL_SELECTIONS.has(selectionValue)) {
      removeKeys.push('platform');
    }

    updateRow(patch, removeKeys.length > 0 ? removeKeys : undefined);
  };


  const handleExpand = () => {
    setExpanded(true)
    setAllCollapsed(false);
  };

  const handleEditBorderBox = (e) => {
    const btn = e.target.id;
    if (edit === btn) {
      setEdit('');
    } else {
      setEdit(btn);
    }
  };

  const collapsedBtnContent = useMemo(() => {
    const iconClass = row.icon || getConnectButtonDefaultIcon(row);
    return (
      <>
      {(row.type !== 'continue' && iconClass) && <i className={iconClass} style={{position: 'absolute', left: '.75rem', width: 'fit-content'}}/>}
      <div>
      <span>{row.text || 'Preview'}</span>
      {(row.type === 'continue' && iconClass) && <i className={iconClass} aria-hidden style={{width: 'fit-content'}} />}
      </div>
      </>
    )
  },[row]);

  const handleBorderBottom = useMemo(() => {
    if (windowWidth <= 500 && expanded) {
      return '1px solid #ddd'
    } else {
      return null
    }
  },[windowWidth, expanded]);

  const handleIcons = useMemo(() => {
    if (row.type) {
      return (
        <>
        {icons(row)?.map((icon, i) => (
          <button type='button' key={i} className={`${styles.iconBtn} ${row.icon === icon ? styles.active : null}`} onClick={() => updateRow({ icon })}>
            <i className={icon} style={{pointerEvents: 'none'}} />
          </button>
        ))}
        </>
      )
    } else {
      return <div className={styles.noTypeMessage}>Please select a button type to display icons.</div>
    }
},[row]);

  return (
    <div className={styles.rowWrapper} ref={setNodeRef} style={{ ...style, borderBottom: handleBorderBottom }} >
      <div className={styles.rowGroup}>
        <AnimatePresence mode='wait' initial={false}>
          {((containerWidth > 470 && windowWidth > 768) || expanded) ? (
            <motion.div
              key='input'
              className={styles.rowContainer}
              transition={transition}
              variants={variants}
              intial='initial'
              animate='animate'
              exit='initial'
            >
              <AnimatePresence initial={false}>
                {(containerWidth <= 470 || windowWidth <= 768) && (
                    <motion.button 
                        className={styles.collapseBtn} 
                        onClick={() => setExpanded(false)}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <i className="far fa-chevron-up" />
                    </motion.button>
                )}
              </AnimatePresence>
              <div className={styles.row}>
                <div className={styles.rowContent}>
                  <div className={styles.rowPreview}>
                    <input
                      className={styles.previewInput}
                      type='text'
                      value={row.text || ''}
                      onChange={(e) => updateRow({ text: e.target.value })}
                      maxLength='200'
                      style={{
                        color: row.color || '#000000',
                        backgroundColor: row.background || '#ffffff',
                        paddingLeft: (row.icon || getConnectButtonDefaultIcon(row)) && row.type !== 'continue' ? '2rem' : null,
                        borderStyle: row.border?.style || 'none',
                        borderWidth: `${row.border?.width || 1}px`,
                        borderColor: row.border?.color || "#dddddd",
                        borderRadius: `${row.border?.radius || 8}px`,
                        boxShadow: `${row.box_shadow?.x || 0}px ${row.box_shadow?.y || 1}px ${row.box_shadow?.blur || 3}px ${row.box_shadow?.color || '#ddd'}`
                      }}
                      placeholder=''
                    />
                    <span className={styles.rowText} style={{color: row.color || '#000000'}}>Preview</span>
                  </div>
                  <div className={styles.colorControls}>
                    <PickerControl 
                        currentColor={row.background} 
                      onChange={(val) => updateRow({ background: val })}
                        title="Background" 
                        type="bg" 
                        className={styles.colorButton}
                    />
                    <PickerControl 
                        currentColor={row.color} 
                      onChange={(val) => updateRow({ color: val })}
                        title="Text Color" 
                        type="text" 
                        className={styles.colorButton}
                    />
                  </div>
                </div>
                <div className={styles.typeGroup} style={{flexWrap: `${containerWidth < 470 ? 'wrap' : 'nowrap'}`}}>
                  <select
                    className={styles.typeSelect}
                    value={getConnectButtonSelectionValue(row)}
                    onChange={handleTypeChange}
                    aria-label='Row type'
                  >
                    <option value="" disabled>Select type</option>
                    {CONNECT_BUTTON_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {connectButtonUsesOriginUrl(row) && (
                    <input
                      className={styles.smallInput}
                      type="text"
                      value="Origin URL"
                      disabled
                      style={{ cursor: 'not-allowed', opacity: 0.6 }}
                    />
                  )}
                  {connectButtonRequiresValue(row) && (
                    <input
                      className={styles.smallInput}
                      type="text"
                      value={row.value || ''}
                      onChange={(e) => updateRow({ value: e.target.value })}
                      placeholder={getConnectButtonValuePlaceholder(row)}
                      maxLength={300}
                    />
                  )}
                  <button 
                    type='button'
                    className={styles.deleteBtn}
                    onClick={() => canDelete && onDelete(row.id)}
                    title={canDelete ? 'Delete Row' : "Can't Delete"}
                    disabled={!canDelete}
                  >
                    <i className='fas fa-trash-can' />
                  </button>
                </div> 
                {/* starts border and box shadow */}
                <AnimatePresence mode='wait' initial={false}>
                  {row.type && (
                    <motion.div 
                      className={styles.borderBoxWrap}
                      transition={transition}
                      variants={variants}
                      initial='initial'
                      animate='animate'
                      exit='initial'
                    >
                      <button type='button' id='border' className={`${styles.borderBoxBtn} ${edit === 'border' ? styles.active : null}`} onClick={handleEditBorderBox}>Border <i className={`far fa-${edit === 'border' ? 'minus' : 'plus'}`} /></button>
                      <button type='button' id='box-shadow' className={`${styles.borderBoxBtn} ${edit === 'box-shadow' ? styles.active : null}`} onClick={handleEditBorderBox}>Box Shadow <i className={`far fa-${edit === 'box-shadow' ? 'minus' : 'plus'}`} /></button>
                      <button type='button' id='icon' className={`${styles.borderBoxBtn} ${edit === 'icon' ? styles.active : null}`} onClick={handleEditBorderBox}>Icon <i className={`far fa-${edit === 'icon' ? 'minus' : 'plus'}`} /></button>
                    </motion.div> 
                  )}
                </AnimatePresence>
                <AnimatePresence mode='wait' initial={false}>
                  {edit === 'border' ? (
                    <motion.div 
                      key='border'
                      className={styles.borderEditWrap}
                      transition={transition}
                      variants={variants}
                      initial='initial'
                      animate='animate'
                      exit='initial'
                    >
                      <div className={styles.borderEditInfo}>Type, Thickness, Border Radius, and Color</div>
                      <div className={styles.borderEditContainer}>
                        <select className={styles.borderStyleSelect} value={row.border.style || '0'} onChange={(e) => updateRow({ border: { ...row.border, style: e.target.value } })}>
                          <option value='0' disabled>-- Type --</option>
                          <option value='none'>None</option>
                          <option value='dotted'>Dotted</option>
                          <option value='dashed'>Dashed</option>
                          <option value='solid'>Solid</option>
                          <option value='double'>Double</option>
                          <option value='groove'>Groove</option>
                          <option value='ridge'>Ridge</option>
                          <option value='inset'>Inset</option>
                          <option value='outset'>Outset</option>
                        </select>
                        <input className={styles.borderWidthInput} type='number' min='0' max='10' value={row.border.width || 1} onChange={(e) => updateRow({ border: { ...row.border, width: e.target.value > 10 ? 10 : e.target.value < 0 ? 0 : e.target.value } })} />
                        <input className={styles.borderRadiusInput} type='number' min='0' max='100' value={row.border.radius || 0} onChange={(e) => updateRow({ border: { ...row.border, radius: e.target.value > 100 ? 100 : e.target.value } })} />
                        <PickerControl
                          currentColor={row.border.color} 
                          onChange={(val) => updateRow({ border: { ...row.border, color: val } })}
                          title="Border Color" 
                          type="border color" 
                          className={styles.borderColorInput}
                        />
                      </div>
                    </motion.div>             
                  ) : edit === 'box-shadow' ? (
                    <motion.div 
                      key='box-shadow'
                      className={styles.boxShadowEditWrap}
                      transition={transition}
                      variants={variants}
                      initial='initial'
                      animate='animate'
                      exit='initial'
                    >
                      <div className={styles.boxShadowEditInfo}>X-Offset, Y-Offset, Blur, and Color</div>
                      <div className={styles.boxShadowEditContainer}>
                        <input className={styles.boxShadowInput} type='number' min='-50' max='50' value={row.box_shadow.x || 0} onChange={(e) => updateRow({ box_shadow: { ...row.box_shadow, x: e.target.value > 50 ? 50 : e.target.value < -50 ? -50 : e.target.value } })} />
                        <input className={styles.boxShadowInput} type='number' min='-50' max='50' value={row.box_shadow.y || 0} onChange={(e) => updateRow({ box_shadow: { ...row.box_shadow, y: e.target.value > 50 ? 50 : e.target.value < -50 ? -50 : e.target.value } })} />
                        <input className={styles.boxShadowInput} type='number' min='0' max='50' value={row.box_shadow.blur || 0} onChange={(e) => updateRow({ box_shadow: { ...row.box_shadow, blur: e.target.value > 50 ? 50 : e.target.value } })} />
                        <PickerControl
                          currentColor={row.box_shadow.color}
                          onChange={(val) => updateRow({ box_shadow: { ...row.box_shadow, color: val } })}
                          title="Box Shadow Color"
                          type='box shadow color'
                          className={styles.boxShadowColorInput}
                        />
                      </div>
                    </motion.div>
                  ) : edit === 'icon' && (
                    <motion.div
                      key='icon'
                      className={styles.iconEditWrap}
                      transition={transition}
                      variants={variants}
                      initial='initial'
                      animate='animate'
                      exit='initial'
                    >
                      <div className={styles.iconEditInfo}>Icon Select</div>
                      <div className={styles.iconEditContainer}>
                        <div className={styles.iconSelect}>
                            {handleIcons}
                            
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ) : (
            <motion.button
              key='button'
              className={styles.previewButton}
              onClick={() => handleExpand()}
              disabled={containerWidth > 470 && windowWidth > 768}
              transition={transition}
              variants={variants}
              initial='initial'
              animate='animate'
              exit='initial'
              style={{
                backgroundColor: row.background || '#ffffff', 
                color: row.color || '#000000',
                borderStyle: row.border?.style || 'none',
                borderWidth: `${row.border?.width || 1}px`,
                borderColor: row.border?.color || '#dddddd',
                borderRadius: `${row.border?.radius || 8}px`,
                boxShadow: `${row.box_shadow?.x || 0}px ${row.box_shadow?.y || 1}px ${row.box_shadow?.blur || 3}px ${row.box_shadow?.color || '#dddddd'}`
              }}
            >
              {collapsedBtnContent}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      <div ref={setActivatorNodeRef} {...listeners} className={styles.dragHandle} title='Drag to Reorder'>
        <i className='fas fa-grip-dots-vertical' />
      </div>
    </div>
  );
};

const PickerControl = ({ currentColor, onChange, title, type, className }) => {
    const btnRef = useRef(null);
    const [ ref, isOpen, setIsOpen ] = useClickOutside(false, btnRef);
  
    return (
        <div ref={ref} style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
                type='button'
                ref={btnRef}
                className={className}
                onClick={() => setIsOpen(!isOpen)}
                style={{ backgroundColor: currentColor }}
                title={title}
            />
            {isOpen && (
                <div className={styles.pickerPortal}>
                    <ColorPicker color={currentColor} onChange={onChange} name={type} />
                </div>
            )}
        </div>
    );
};